<script setup>
import {
  computed,
  inject,
  nextTick,
  onMounted,
  reactive,
  ref,
  shallowRef,
  toValue,
  watch,
} from 'vue'
import { result } from 'lodash-es'
import { format } from 'date-fns'
import { createReusableTemplate } from '@vueuse/core'
import DOMpurify from 'dompurify'
import mustache from 'mustache'

import { downloadLink, useApi } from '@provise/composables/api'
import { useTransfer } from '@/composables/complaints'
import { useMessageStack } from '@provise/stores/messagestack'
import { commons } from '@provise/lib/transformer'

import Mod from '@provise/components/mod.vue'
import SelectId from '@/components/select/selectIdObject.vue'
import Select from '@/components/select/select.vue'
import Editor from '@/components/editor/editor.vue'
import SimpleSchema from '@/components/simpleSchema.vue'
import Form from '@provise/components/form.vue'

import statusesInit from './forms'
import Step from '../step.vue'
import Uploads from '../uploads.vue'

const allStatuses = [
  'request_sent',
  'request_received',
  'transfer_started',
  'transfer_received',
  'done',
]
defineEmits(['close'])

const { model: departments } = useApi({
  url: 'departments',
  immediate: true,
  data: [],
  pick: d => d.data || [],
})

const complaint = inject('complaint')
const statuses = statusesInit({ departments })

const { transfer, transfers, fetchTransfer, fetchTransfers, approveTransfer } = useTransfer()
const acceptable = computed(() => {
  let acc = true
  if (transfer.value?.status?.key == 'transfer_started' && !transfer.value?.email_reception) {
    acc = false
  }
  return acc
})

const tpl = ref(null)
const uploadsSections = {
  prepare: 'Отправка запроса',
  request_sent: 'Информация о получении образца',
  transfer_started: 'Отправка на ресепшн',
}
const { push: notify } = useMessageStack()

const { model: etemplates } = useApi({
  url: 'email_templates',
  immediate: true,
  data: [],
  pick: d => d.data || [],
})
const tid = ref(null)

const troptions = computed(() => {
  let t = [...transfers.value]
  t = t.map(t => {
    let res = { ...t }
    res.label = `от ${format(t.date_created, 'dd.MM.y H:mm')}`
    return res
  })
  t.sort((a, b) => (a.date_created > b.date_created ? 1 : -1))
  return t
})

const currentStatus = computed(() => {
  const key = transfer.value?.status?.key

  if (!key) {
    return null
  }
  return {
    key,
    ...statuses[key],
  }
})

const stepseditable = reactive({})
const stepsvisible = reactive({})

const [stepTplDefine, stepTpl] = createReusableTemplate()
const schInit = ref([
  [
    null,
    mv => {
      let n = result(mv, 'receiver.name')
      let m = result(mv, 'receiver.email')

      if (!n) {
        return
      }
      return `${n} (${m})`
    },
    'Получатель',
  ],
  [null, mv => [DOMpurify.sanitize(mv.content || '')], 'Текст сообщения'],
  [
    null,
    mv => {
      console.log(statuses)
      if (!(Array.isArray(mv.uploads) && mv.uploads.length)) {
        return
      }
      const groupedUploads = mv.uploads.reduce((memo, f) => {
        if (!memo[f.division]) {
          memo[f.division] = []
        }
        memo[f.division].push(f)
        return memo
      }, {})
      let result = ['<div class="vstack">']

      for (const [section, sectionLabel] of Object.entries(uploadsSections)) {
        const files = groupedUploads[`transfer.${section}`]
        if (!Array.isArray(files) || !files.length) {
          continue
        }

        result = [
          ...result,
          '<div class="mb-3 vstack">',
          `<h6 class="text-muted mb-0">${sectionLabel}</h6>`,
          ...files.map(
            ({ filename, secure_filename }) =>
              `<a href="${downloadLink({ url: secure_filename })}" download="${filename}">${filename}</a>`
          ),
          '</div>',
        ]
      }
      result = [...result, '</div>']
      return result
    },
    'Отправленные файлы',
  ],
  [null, mv => commons.dateLocal(mv.date_request_sent, 'Pp'), 'Отправлено'],
])

const uploadUrl = computed(
  () => `complaints/${complaint.value?.id}/transfers/${transfer.value.id}/upload`
)

const initialEdit = computed(() => {
  const id = transfer.value.id
  const stk = transfer.value?.status?.key

  return !id || stk == 'prepare'
})

let formref = shallowRef(null)

watch(tid, async id => {
  if (!id) {
    return
  }

  transfer.value = { id }
  await nextTick()
  await fetchTransfer({ method: 'get' })
})

watch(tpl, tpl => {
  const tep = etemplates.value.find(tv => tpl == tv.id)
  if (!tep) {
    return
  }
  transfer.value = { ...transfer.value, content: tep.text }
})

watch(
  transfer,
  trv => {
    tid.value = trv.id
  },
  { immediate: true }
)

watch(
  currentStatus,
  cs => {
    const csk = cs?.key
    const currentstepidx = allStatuses.indexOf(csk)

    if (csk == 'done') {
      Object.keys(stepsvisible).forEach(k => {
        if (k == 'done') {
          return
        }
        stepsvisible[k] = true
      })
      return
    }

    for (let i = 0; i < allStatuses.length; i++) {
      let sk = allStatuses[i]
      stepsvisible[sk] = i <= currentstepidx
      stepseditable[sk] = i == currentstepidx
    }
  },
  { immediate: true }
)

onMounted(() => {
  fetchTransfers()
})

function onReceiver(val) {
  const rid = val?.id
  if (!rid) {
    return
  }
  const dep = (departments.value || []).find(d => rid == d.id)
  if (!dep || !dep.communication_template?.length) {
    return
  }
  const model = toValue(complaint)
  model.medicine = commons.medicineLabel(model.medicine)
  transfer.value = {
    ...transfer.value,
    content: mustache.render(dep.communication_template, model),
  }
  tpl.value = null
}

async function onSave() {
  const t = { ...transfer.value }
  const apicfg = { data: t }
  if (!t.id) {
    apicfg.method = 'post'
  }
  await fetchTransfer(apicfg)
  notify('Трансфер сохранён')
  await fetchTransfers()
}

async function onFormApply(cb) {
  await onSave()
  if (cb) {
    cb()
  }
  formref.value?.reset()
}

async function onFormReset() {
  await fetchTransfer({ method: 'get' })
}
</script>
<template>
  <Mod @hidden="$emit('close')">
    <template #header>
      Детали трансфера образца

      <span class="badge text-bg-secondary ms-3" v-if="transfer?.status?.key == 'done'"
        >Завершен</span
      >

      <div v-if="troptions.length && tid" class="flex-grow-1 mx-3">
        <Select :options="troptions" v-model="tid" values="label" v-if="troptions.length > 1" />
        <span v-else>{{ troptions[0].label }}</span>
      </div>
    </template>

    <div :class="{ card: transfer.id }">
      <div :class="{ 'card-body': transfer.id }">
        <Step :edit="initialEdit" :sch="[]" :isch="schInit" v-model="transfer">
          <Form
            entity-name="transfers"
            :entity="transfer"
            @form:apply="onFormApply"
            @form:reset="onFormReset"
            ref="formref"
          >
            <div class="vstack gap-3">
              <div>
                <label class="form-label">Укажите получателей</label>

                <SelectId
                  :options="departments"
                  v-model="transfer.receiver"
                  @update:model-value="onReceiver"
                />
              </div>

              <div class="h5">Введите сопроводительный текст в электронной почте</div>

              <div class="d-flex gap-2 align-items-center">
                <label class="form-label mb-0"> Использовать шаблон </label>
                <Select v-model="tpl" :options="etemplates" class="flex-grow-1" />
              </div>

              <Editor v-model="transfer.content" />

              <div v-if="!transfer.id" class="alert alert-light">
                <i class="bi bi-file-earmark" />
                Чтобы приложить файлы, сохраните запрос
              </div>
              <Uploads v-else division="transfer.prepare" v-model="transfer" :url="uploadUrl" />
            </div>

            <template #controls-rev>
              <button type="button" class="btn btn-warning" @click="approveTransfer">
                <i class="bi bi-check-square"></i>
                Отправить запрос
              </button>
            </template>
          </Form>
        </Step>
      </div>
    </div>

    <stepTplDefine #="{ status, stKey }">
      <div>
        <Step
          :edit="stepseditable[stKey]"
          :sch="status.form"
          v-model="transfer"
          :isch="status.info || {}"
        >
          <Form
            entity-name="transfers"
            :entity="transfer"
            @form:apply="onFormApply"
            @form:reset="onFormReset"
            ref="formref"
          >
            <div class="form-schema">
              <SimpleSchema v-model="transfer" :simplsch="status.form" />
            </div>
            <template v-if="['request_sent', 'transfer_started'].includes(stKey)">
              <Uploads :division="`transfer.${stKey}`" v-model="transfer" :url="uploadUrl" />
            </template>
            <template
              #controls-rev
              v-if="status.key != 'done' && status.key != 'transfer_received'"
            >
              <button
                type="button"
                class="btn btn-warning"
                @click="approveTransfer"
                :disabled="!acceptable"
              >
                <i class="bi bi-check-square"></i>
                {{ status.acceptLabel }}
              </button>
            </template>
          </Form>
        </Step>
      </div>
    </stepTplDefine>

    <template v-if="transfer.id">
      <div class="vstack gap-3 mt-3">
        <template v-for="(status, stKey) of statuses" :key="stKey">
          <template v-if="stepsvisible[stKey]">
            <template v-if="!status.sectionLabel">
              <stepTpl :st-key="stKey" :status="status" />
            </template>
            <div class="card" v-else>
              <div class="card-body">
                <div class="h5" v-if="status.sectionLabel">{{ status.sectionLabel }}</div>
                <stepTpl :st-key="stKey" :status="status" />
              </div>
            </div>
          </template>
        </template>
      </div>
      <template v-if="currentStatus?.key == 'transfer_received'">
        <button
          type="button"
          class="btn btn-outline-warning align-self-start"
          @click="approveTransfer"
        >
          <i class="bi bi-check-square"></i>
          Завершить трансфер
        </button>
      </template>
    </template>
  </Mod>
</template>
